import { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Book, BookOpen, Sparkles, X } from 'lucide-react';
import type { InspirationSource } from '@/hooks';
import yimLogo from '@/assets/images/logo.webp';
import { StoryControls } from './StoryControls';
import type { InspirationData } from './types';

const sourceConfig: Record<InspirationSource, {
  labelKey: string;
  icon: typeof Book;
  gradient: string;
}> = {
  bible: {
    labelKey: 'components.inspiration.sources.bible',
    icon: Book,
    gradient: 'from-amber-400 via-orange-500 to-amber-600',
  },
  quran: {
    labelKey: 'components.inspiration.sources.quran',
    icon: BookOpen,
    gradient: 'from-emerald-400 via-teal-500 to-emerald-600',
  },
  leadership: {
    labelKey: 'components.inspiration.sources.leadership',
    icon: Sparkles,
    gradient: 'from-blue-400 via-indigo-500 to-blue-600',
  },
};

function getFullscreenTextSize(contentLength: number): string {
  if (contentLength > 300) return 'text-xl sm:text-2xl md:text-3xl';
  if (contentLength > 200) return 'text-2xl sm:text-3xl md:text-4xl';
  if (contentLength > 100) return 'text-3xl sm:text-4xl md:text-5xl';
  return 'text-4xl sm:text-5xl md:text-6xl';
}

interface StoryOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  inspiration: InspirationData;
}

export function StoryOverlay({ isOpen, onClose, inspiration }: StoryOverlayProps) {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const config = sourceConfig[inspiration.source];
  const SourceIcon = config.icon;

  const textSizeClass = useMemo(() => {
    return getFullscreenTextSize(inspiration.content.length);
  }, [inspiration.content]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Animated gradient background */}
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />

            <motion.div
              className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-30 bg-gradient-to-br from-blue-800 to-blue-500"
              animate={{ x: [0, 50, 0], y: [0, -30, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-30 bg-gradient-to-br from-amber-400 to-amber-500"
              animate={{ x: [0, -40, 0], y: [0, 40, 0], scale: [1, 1.3, 1] }}
              transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>

          {/* UI Layer */}
          <div className="relative z-10 w-full h-full">
            {/* Close button */}
            <motion.button
              onClick={onClose}
              className="absolute top-6 right-6 p-3 rounded-full transition-colors z-20 bg-white/10 text-white/80"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="w-6 h-6 text-inherit" />
            </motion.button>

            {/* Progress bar */}
            <div className="absolute top-4 left-4 right-4 flex gap-1 z-20">
              <motion.div className="h-1 rounded-full flex-1 overflow-hidden bg-white/30">
                <motion.div
                  className="h-full rounded-full bg-white"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 15, ease: 'linear' }}
                  onAnimationComplete={onClose}
                />
              </motion.div>
            </div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20"
            >
              <StoryControls
                inspiration={inspiration}
                contentRef={contentRef}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
              />
            </motion.div>

            {/* Shareable Card */}
            <div
              ref={contentRef}
              className="absolute inset-0 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 bg-slate-900"
            >
              {/* Source badge */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wider shadow-lg mb-8 text-white"
                style={{
                  background: inspiration.source === 'bible'
                    ? 'linear-gradient(to right, #fbbf24, #f97316, #d97706)'
                    : inspiration.source === 'quran'
                    ? 'linear-gradient(to right, #34d399, #14b8a6, #059669)'
                    : 'linear-gradient(to right, #60a5fa, #6366f1, #2563eb)',
                }}
              >
                <SourceIcon className="w-4 h-4 text-white" />
                <span className="text-white">{t(config.labelKey)}</span>
              </motion.div>

              {/* Quote */}
              <motion.blockquote
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 100 }}
                className="text-center max-w-4xl"
              >
                <p className={`${textSizeClass} font-serif italic leading-tight tracking-tight text-white`}>
                  &ldquo;{inspiration.content}&rdquo;
                </p>
              </motion.blockquote>

              {/* Reference */}
              {inspiration.reference && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-8 text-lg sm:text-xl font-semibold text-amber-400"
                >
                  — {inspiration.reference}
                </motion.p>
              )}

              {/* YIM Branding */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="absolute bottom-20 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={yimLogo}
                    alt="Logo YIM"
                    width={40}
                    height={40}
                    loading="lazy"
                    decoding="async"
                    className="w-10 h-10 rounded-full object-cover shadow-lg"
                  />
                  <div className="text-left">
                    <p className="text-white font-bold text-sm">{t('common.splash.title')}</p>
                    <p className="text-white/60 text-xs">Burundi • {t('components.inspiration.dailyLabel')}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
